"use client";

import useSWR, { mutate as globalMutate } from "swr";
import { useAuth } from "@clerk/nextjs";
import { useCallback } from "react";
import { createFetcher } from "@/lib/api";
import type {
  DispatchStrategiesResponse,
  DispatchStrategy,
  DispatchStrategyInput,
} from "@/lib/api-types";

// Strategies are user-named, user-created rows. Workflows reference them
// by id; orgs pick one as "default" for unmatched orders. The hook
// surface mirrors loads/orders/drivers (list, detail, mutations) so the
// strategies page can reuse the master/detail pattern.

const LIST_KEY = "/api/v1/strategies";
const DEFAULT_KEY = "/api/v1/strategies/default";

async function authHeaders(
  getToken: () => Promise<string | null>,
): Promise<HeadersInit> {
  const token = await getToken();
  return {
    "Content-Type": "application/json",
    ...(token ? { Authorization: `Bearer ${token}` } : {}),
  };
}

async function parseError(res: Response, fallback: string): Promise<never> {
  const body = (await res.json().catch(() => ({}))) as { error?: string };
  throw new Error(body.error ?? `${fallback} (${res.status})`);
}

export function useStrategies(includeArchived = false) {
  const { getToken } = useAuth();
  const key = includeArchived
    ? `${LIST_KEY}?include_archived=true`
    : LIST_KEY;
  const { data, error, isLoading, mutate } = useSWR<DispatchStrategiesResponse>(
    key,
    createFetcher(getToken),
  );

  const strategies = data?.strategies ?? [];
  const byID = new Map<string, DispatchStrategy>();
  for (const s of strategies) {
    byID.set(s.id, s);
  }

  return {
    strategies,
    byID,
    isLoading,
    error,
    mutate,
  };
}

export function useStrategy(id: string | null) {
  const { getToken } = useAuth();
  const { data, error, isLoading, mutate } = useSWR<DispatchStrategy>(
    id ? `${LIST_KEY}/${id}` : null,
    createFetcher(getToken),
  );
  return {
    strategy: data,
    isLoading,
    error,
    mutate,
  };
}

// useDefaultStrategy returns the org's fallback strategy used when no
// workflow predicate matches. Returns null when none is set.
export function useDefaultStrategy() {
  const { getToken } = useAuth();
  const { data, error, isLoading } = useSWR<DispatchStrategy | null>(
    DEFAULT_KEY,
    async (url: string) => {
      const fetcher = createFetcher(getToken);
      try {
        return await fetcher<DispatchStrategy>(url);
      } catch (e) {
        // 404 means "no default set" — treat as null, not an error.
        if (e instanceof Error && e.message.toLowerCase().includes("not found")) {
          return null;
        }
        throw e;
      }
    },
  );
  return {
    defaultStrategy: data ?? null,
    isLoading,
    error,
  };
}

export function useStrategyMutations() {
  const { getToken } = useAuth();

  const refresh = useCallback(async () => {
    await Promise.all([
      globalMutate(LIST_KEY),
      globalMutate(`${LIST_KEY}?include_archived=true`),
      globalMutate(DEFAULT_KEY),
      globalMutate(
        (key) => typeof key === "string" && key.startsWith(`${LIST_KEY}/`),
        undefined,
        { revalidate: true },
      ),
    ]);
  }, []);

  const create = useCallback(
    async (input: DispatchStrategyInput): Promise<DispatchStrategy> => {
      const res = await fetch(LIST_KEY, {
        method: "POST",
        headers: await authHeaders(getToken),
        body: JSON.stringify(input),
      });
      if (!res.ok) await parseError(res, "Failed to create strategy");
      const body = (await res.json()) as DispatchStrategy;
      await refresh();
      return body;
    },
    [getToken, refresh],
  );

  const update = useCallback(
    async (
      id: string,
      input: DispatchStrategyInput,
    ): Promise<DispatchStrategy> => {
      const res = await fetch(`${LIST_KEY}/${id}`, {
        method: "PUT",
        headers: await authHeaders(getToken),
        body: JSON.stringify(input),
      });
      if (!res.ok) await parseError(res, "Failed to update strategy");
      const body = (await res.json()) as DispatchStrategy;
      await refresh();
      return body;
    },
    [getToken, refresh],
  );

  const archive = useCallback(
    async (id: string): Promise<void> => {
      const res = await fetch(`${LIST_KEY}/${id}/archive`, {
        method: "POST",
        headers: await authHeaders(getToken),
      });
      if (!res.ok) await parseError(res, "Failed to archive strategy");
      await refresh();
    },
    [getToken, refresh],
  );

  const setDefault = useCallback(
    async (id: string): Promise<void> => {
      const res = await fetch(`${LIST_KEY}/${id}/default`, {
        method: "POST",
        headers: await authHeaders(getToken),
      });
      if (!res.ok) await parseError(res, "Failed to set default strategy");
      await refresh();
    },
    [getToken, refresh],
  );

  return { create, update, archive, setDefault };
}
